import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useHasRole } from "@/hooks/useHasRole";
import { supabase } from "@/integrations/supabase/client";
import { AppLayout } from "@/components/layout/AppLayout";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { RefreshCw, CheckCircle, AlertCircle, ArrowRightLeft } from "lucide-react";

interface LegacySubscription {
  id: string;
  email: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  status: string;
  current_period_end: string | null;
  quantity: number | null;
}

interface CatalogDiffItem {
  legacy_price_id: string;
  legacy_product_id?: string;
  legacy_product_name: string;
  legacy_unit_amount: number | null;
  legacy_currency: string;
  legacy_interval: string | null;
  legacy_interval_count?: number | null;
  legacy_billing_scheme?: string | null;
  legacy_tiers_mode?: string | null;
  legacy_tiers?: Array<{ up_to: number | null; unit_amount: number | null; flat_amount: number | null }> | null;
  legacy_nickname?: string | null;
  pj_price_id?: string;
  pj_product_id?: string;
}

interface SyncReport {
  to_create: CatalogDiffItem[];
  existing: Array<{
    legacy_price_id: string;
    pj_price_id: string;
    pj_product_id: string;
    legacy_product_name: string;
  }>;
  errors: Array<{ legacy_price_id: string; message: string }>;
  applied?: boolean;
}

export default function AdminStripeMigrationPage() {
  const hasRole = useHasRole("careos_admin");
  const roleLoading = hasRole === null;

  const [activeTab, setActiveTab] = useState("catalog");
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogDiff, setCatalogDiff] = useState<SyncReport | null>(null);
  const [subsLoading, setSubsLoading] = useState(false);
  const [legacySubs, setLegacySubs] = useState<LegacySubscription[]>([]);

  const fetchCatalogDiff = async (mode: "dry_run" | "apply") => {
    setCatalogLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-sync-stripe-catalog", {
        body: { mode },
      });
      if (error) throw error;
      setCatalogDiff(data as SyncReport);
      if (mode === "apply") {
        toast.success("Catálogo aplicado na PJ", {
          description: `${(data as SyncReport).to_create.length} preços criados, ${(data as SyncReport).existing.length} já existiam.`,
        });
      }
    } catch (e: any) {
      toast.error("Erro ao sincronizar catálogo", {
        description: e?.message ?? "Verifique os logs da edge function.",
      });
    } finally {
      setCatalogLoading(false);
    }
  };

  const fetchLegacySubscriptions = async () => {
    setSubsLoading(true);
    try {
      const { data, error } = await supabase
        .from("subscriptions")
        .select("id, email, stripe_subscription_id, stripe_customer_id, status, current_period_end, quantity")
        .eq("stripe_env", "live_pf_legacy")
        .order("updated_at", { ascending: false });
      if (error) throw error;
      setLegacySubs((data as LegacySubscription[]) ?? []);
    } catch (e: any) {
      toast.error("Erro ao carregar assinaturas legacy", {
        description: e?.message,
      });
    } finally {
      setSubsLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "subscriptions" && legacySubs.length === 0 && !subsLoading) {
      fetchLegacySubscriptions();
    }
  }, [activeTab]);

  const currencyDisplay = (amount: number | null, currency: string) => {
    if (amount == null) return "—";
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const catalogRows = useMemo(() => {
    if (!catalogDiff) return [];
    const existingAsDiff: CatalogDiffItem[] = catalogDiff.existing.map((e) => ({
      legacy_price_id: e.legacy_price_id,
      legacy_product_name: e.legacy_product_name,
      legacy_unit_amount: null,
      legacy_currency: "",
      legacy_interval: null,
      pj_price_id: e.pj_price_id,
      pj_product_id: e.pj_product_id,
    }));
    return [...catalogDiff.to_create, ...existingAsDiff];
  }, [catalogDiff]);

  if (roleLoading) {
    return (
      <AppLayout>
        <div className="p-4 space-y-4">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AppLayout>
    );
  }

  if (!hasRole) {
    return <Navigate to="/app" replace />;
  }

  return (
    <AppLayout>
      <div className="p-4 space-y-6">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">Migração Stripe PF → PJ</h1>
          <p className="text-muted-foreground">
            Prepare e execute a migração da conta Stripe Pessoa Física para a conta Jurídica.
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="catalog">Etapa 1 — Catálogo</TabsTrigger>
            <TabsTrigger value="subscriptions">Etapa 2 — Assinaturas</TabsTrigger>
          </TabsList>

          <TabsContent value="catalog" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Catálogo de preços</CardTitle>
                <CardDescription>
                  Compare os produtos/preços ativos na conta PF com a PJ e aplique a migração.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  <Button
                    onClick={() => fetchCatalogDiff("dry_run")}
                    disabled={catalogLoading}
                    variant="outline"
                  >
                    <RefreshCw className={`mr-2 h-4 w-4 ${catalogLoading ? "animate-spin" : ""}`} />
                    Simular
                  </Button>
                  <Button
                    onClick={() => fetchCatalogDiff("apply")}
                    disabled={catalogLoading}
                  >
                    <ArrowRightLeft className="mr-2 h-4 w-4" />
                    Aplicar
                  </Button>
                </div>

                {catalogDiff && (
                  <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                      <Badge variant="secondary">
                        Criar: {catalogDiff.to_create.length}
                      </Badge>
                      <Badge variant="outline">
                        Já existem: {catalogDiff.existing.length}
                      </Badge>
                      {catalogDiff.errors.length > 0 && (
                        <Badge variant="destructive">
                          Erros: {catalogDiff.errors.length}
                        </Badge>
                      )}
                      {catalogDiff.applied && (
                        <Badge className="bg-success text-success-foreground">
                          <CheckCircle className="mr-1 h-3 w-3" />
                          Aplicado
                        </Badge>
                      )}
                    </div>

                    {catalogDiff.errors.length > 0 && (
                      <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                        <div className="font-medium flex items-center gap-1 mb-1">
                          <AlertCircle className="h-4 w-4" />
                          Erros na aplicação
                        </div>
                        <ul className="list-disc list-inside space-y-1">
                          {catalogDiff.errors.map((err, idx) => (
                            <li key={idx}>{err.legacy_price_id}: {err.message}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="border rounded-md overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Produto PF</TableHead>
                            <TableHead>Price PF</TableHead>
                            <TableHead>Valor / Moeda</TableHead>
                            <TableHead>Intervalo</TableHead>
                            <TableHead>Status na PJ</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {catalogRows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                                Nenhum preço ativo encontrado na PF.
                              </TableCell>
                            </TableRow>
                          ) : (
                            catalogRows.map((row) => {
                              const isExisting = !!row.pj_price_id;
                              return (
                                <TableRow key={row.legacy_price_id}>
                                  <TableCell className="font-medium">
                                    <div>{row.legacy_product_name}</div>
                                    {row.legacy_nickname && (
                                      <div className="text-xs text-muted-foreground">{row.legacy_nickname}</div>
                                    )}
                                    {row.legacy_product_id && (
                                      <div className="font-mono text-xs text-muted-foreground">{row.legacy_product_id}</div>
                                    )}
                                  </TableCell>
                                  <TableCell className="font-mono text-xs">{row.legacy_price_id}</TableCell>
                                  <TableCell>
                                    {row.legacy_unit_amount != null ? (
                                      currencyDisplay(row.legacy_unit_amount, row.legacy_currency)
                                    ) : row.legacy_tiers && row.legacy_tiers.length > 0 ? (
                                      <div className="space-y-0.5 text-xs">
                                        <div className="text-muted-foreground capitalize">
                                          Escalonado ({row.legacy_tiers_mode ?? "graduated"})
                                        </div>
                                        {row.legacy_tiers.map((t, i) => (
                                          <div key={i}>
                                            até {t.up_to ?? "∞"}:{" "}
                                            {currencyDisplay(t.unit_amount, row.legacy_currency)}
                                            {t.flat_amount != null
                                              ? ` + ${currencyDisplay(t.flat_amount, row.legacy_currency)}`
                                              : ""}
                                          </div>
                                        ))}
                                      </div>
                                    ) : isExisting ? (
                                      "—"
                                    ) : (
                                      <span className="text-xs text-muted-foreground capitalize">
                                        {row.legacy_billing_scheme ?? "—"}
                                      </span>
                                    )}
                                  </TableCell>
                                  <TableCell className="capitalize">
                                    {row.legacy_interval
                                      ? `${row.legacy_interval_count && row.legacy_interval_count > 1 ? `${row.legacy_interval_count}x ` : ""}${row.legacy_interval}`
                                      : "—"}
                                  </TableCell>
                                  <TableCell>
                                    {isExisting ? (
                                      <Badge variant="outline" className="gap-1">
                                        <CheckCircle className="h-3 w-3" />
                                        {row.pj_price_id}
                                      </Badge>
                                    ) : (
                                      <Badge variant="secondary">Pendente</Badge>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="subscriptions" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Assinaturas em PF (legacy)</CardTitle>
                <CardDescription>
                  Lista de assinaturas ainda ativas na conta Pessoa Física. A migração individual será habilitada na Fase D.3.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="border rounded-md overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Email</TableHead>
                        <TableHead>Subscription</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Período atual</TableHead>
                        <TableHead>Qty</TableHead>
                        <TableHead>Ação</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {subsLoading ? (
                        <TableRow>
                          <TableCell colSpan={6} className="py-6">
                            <Skeleton className="h-6 w-full" />
                          </TableCell>
                        </TableRow>
                      ) : legacySubs.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} className="text-center text-muted-foreground py-6">
                            Nenhuma assinatura legacy encontrada.
                          </TableCell>
                        </TableRow>
                      ) : (
                        legacySubs.map((sub) => (
                          <TableRow key={sub.id}>
                            <TableCell>{sub.email}</TableCell>
                            <TableCell className="font-mono text-xs">{sub.stripe_subscription_id}</TableCell>
                            <TableCell>
                              <Badge variant={sub.status === "active" ? "default" : "secondary"}>
                                {sub.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {sub.current_period_end
                                ? new Date(sub.current_period_end).toLocaleDateString("pt-BR")
                                : "—"}
                            </TableCell>
                            <TableCell>{sub.quantity ?? "—"}</TableCell>
                            <TableCell>
                              <Button size="sm" variant="outline" disabled>
                                Migrar agora
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
}
